import React, { useMemo } from "react";
import { usePictureSets } from "../../../hooks/usePictureSets";
import AppBar from "../../Components/AppBar";
import MobilePage, { PageHeader } from "../../../Components/MobilePage";
import { Trans, useTranslation } from "react-i18next";
import MDContainer from "@material-ui/core/Container";
import {
  PRODUCT_DIGITAL_FILE_UUID,
  PRODUCT_DIGITAL_GROUP_FILE_UUID,
  PRODUCT_DIGITAL_MINI_GROUP_FILE_UUID,
  PRODUCT_DIGITAL_SET_UUID,
  usePriceList,
} from "../../../providers/PriceListProvider";
import Card from "@material-ui/core/Card";
import Box from "@material-ui/core/Box";
import makeStyles from "@material-ui/core/styles/makeStyles";
import EpmsImage from "../../../../components/Common/EpmsImage";
import CardContent from "@material-ui/core/CardContent";
import Button from "@material-ui/core/Button";
import { Link } from "react-router-dom";
import { generatePath } from "react-router";
import useRoutes from "../../../hooks/useRoutes";
import { useShoppingCart } from "../../../providers/ShoppingCartProvider";
import { usePortal } from "../../../../Providers/PortalProvider";
import { formatCurrency } from "../../../../utils";
import Divider from "@material-ui/core/Divider";
import { callAPI } from "../../../../lib/common";
import usePictureSetsAvailableToBuyDigitally from "../../../hooks/usePictureSetsAvailableToBuyDigitally";

const useStyles = makeStyles((theme) => ({
  favoriteImageContainer: {
    width: "100px",
    height: "135px",
    border: "1px solid #B5B5B57F",
    marginRight: "16px",
    backgroundColor: "#B5B5B5AF",
    flexShrink: 0,
    [theme.breakpoints.up("sm")]: {
      width: "150px",
      height: "202px",
    },
    "& > img": {
      objectFit: "cover",
      width: "100%",
      height: "100%",
    },
  },
  subjectName: {
    fontSize: "20px",
    fontWeight: 500,
  },
  pictureList: {
    margin: 0,
    padding: 0,
    listStyle: "none",
    overflowX: "auto",
    display: "flex",
    "& > li": {
      height: "50px",
      marginRight: theme.spacing(1),
      display: "inline-block",
      border: "1px solid #b5b5b5",
      "& > img": {
        height: "100%",
      },
      [theme.breakpoints.up("sm")]: {
        height: "100px",
      },
    },
  },
  pictureListContainer: {
    width: "100%",
    overflow: "hidden",
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  infoAndPicturesContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    minWidth: 0,
  },
  cardActions: {
    padding: theme.spacing(2),
  },
  textContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  productDescription: {
    display: "flex",
    alignItems: "flex-end",
    fontSize: "16px",
    fontWeight: "bold",
  },
  oldPrice: {
    color: "rgba(0, 0, 0, 0.6)",
    textDecoration: "line-through",
    textAlign: "right",
  },
  currentPrice: {
    color: theme.palette.secondary.main,
    fontWeight: "bold",
    fontSize: "16px",
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  rightButton: {
    paddingRight: 0,
  },
}));

export default () => {
  const { getProductByUuid } = usePriceList();
  const availablePictureSetForBuying = usePictureSetsAvailableToBuyDigitally();
  const digitalSetProduct = useMemo(() => {
    return getProductByUuid("9c99830e-b4ae-4afb-b1cf-b6f445c1806d");
  }, []);

  const digitalFileProduct = useMemo(() => {
    return getProductByUuid("06e3c44b-7c63-4020-b4ac-42b5333b8b4a");
  }, []);

  return (
    <MobilePage>
      <AppBar />
      <PageHeader>
        <Trans>Buy digital pictures</Trans>
      </PageHeader>
      <MDContainer maxWidth="md">
        {availablePictureSetForBuying &&
          availablePictureSetForBuying.map((pictureSet) => {
            return (
              <PictureSet
                key={pictureSet.subject.uuid}
                pictureSet={pictureSet}
                digitalSetProduct={digitalSetProduct}
                digitalFileProduct={digitalFileProduct}
              />
            );
          })}
      </MDContainer>
    </MobilePage>
  );
};

const PictureSet = ({ pictureSet }) => {
  const { update: updatePictureSets } = usePictureSets();
  const { subject } = pictureSet;
  const classes = useStyles();
  const routes = useRoutes();
  const { updateCart, qualifyForDigitalRebate } = useShoppingCart();
  const portal = usePortal();
  const { digitalRebate } = portal.webshop;
  const { getProductByUuid } = usePriceList();
  const { t } = useTranslation();

  const toggleSetInCart = () => {
    const ps = pictureSet;
    return () => {
      console.log("The picture set", ps);
      callAPI("/ajax/cart/toggle_digital_set_in_cart.php", "POST", {
        pictureSet: ps.subject.uuid,
      }).then((data) => {
        if (window.isEpmsAnalyticsFeatureEnabled && window.epmsAnalytics) {
          window.epmsAnalytics.onCartChange(data);
        }
        updatePictureSets();
        updateCart();
      });
    };
  };

  const digitalSetProduct = getProductByUuid(PRODUCT_DIGITAL_SET_UUID);
  const digitalPortraitProduct = getProductByUuid(PRODUCT_DIGITAL_FILE_UUID);
  const digitalMiniGroupProduct = getProductByUuid(
    PRODUCT_DIGITAL_MINI_GROUP_FILE_UUID,
  );
  const digitalGroupProduct = getProductByUuid(
    PRODUCT_DIGITAL_GROUP_FILE_UUID,
  );

  const favoritePicture = useMemo(() => {
    return (
      pictureSet &&
      pictureSet.pictures.find(
        (picture) => picture.keywords.indexOf("favorite") >= 0,
      )
    );
  }, [pictureSet]);

  const pictures = useMemo(() => {
    return (
      pictureSet &&
      pictureSet.pictures.filter((picture) => {
        if (picture.bought) {
          return false;
        }

        if (picture.pictureType === "IP" && pictureSet.bought) {
          return false;
        }

        if (picture.pictureType === "IP" && !digitalPortraitProduct) {
          return false;
        }
        if (picture.pictureType === "MGP" && !digitalMiniGroupProduct) {
          return false;
        }
        if (picture.pictureType === "GP" && !digitalGroupProduct) {
          return false;
        }
        return picture.uuid !== favoritePicture?.uuid;
      })
    );
  }, [pictureSet, favoritePicture]);

  const hasNonIPFilesForSale = useMemo(() => {
    return pictures.some((picture) => picture.pictureType !== "IP");
  }, [pictures]);

  const singleFileProducts = [];
  if (singleFileProducts) {
    if (digitalPortraitProduct) {
      singleFileProducts.push(digitalPortraitProduct);
    }
    if (digitalMiniGroupProduct) {
      singleFileProducts.push(digitalMiniGroupProduct);
    }
    if (digitalGroupProduct) {
      singleFileProducts.push(digitalMiniGroupProduct);
    }
  }

  let rebatedSetPrice = null;
  if (qualifyForDigitalRebate && digitalRebate.rebatedDigitalSetPrice > 0) {
    rebatedSetPrice = digitalRebate.rebatedDigitalSetPrice;
  } else if (digitalSetProduct?.productprices[0].campaignprice) {
    rebatedSetPrice = digitalSetProduct.productprices[0].campaignprice;
  }

  console.log("Digital portraits products", digitalPortraitProduct);

  let rebatedPortraitFilePrice = null;
  if (singleFileProducts.length === 1 && digitalPortraitProduct) {
    if (qualifyForDigitalRebate && digitalRebate.rebatedDigitalFilePrice > 0) {
      rebatedPortraitFilePrice = digitalRebate.rebatedDigitalFilePrice;
    } else if (digitalPortraitProduct.productprices[0].campaignprice) {
      rebatedPortraitFilePrice =
        digitalPortraitProduct.productprices[0].campaignprice;
    }
  }

  //    digitalMiniGroupProduct || digitalGroupProduct || digitalPortraitProduct;

  return (
    <Card classes={{ root: classes.card }}>
      <CardContent>
        <Box display="flex">
          <div className={classes.favoriteImageContainer}>
            {favoritePicture && <EpmsImage uuid={favoritePicture.uuid} />}
          </div>
          <div className={classes.infoAndPicturesContainer}>
            <div>
              <div>{pictureSet.year}</div>
              <div className={classes.subjectName}>{subject.name}</div>
              <div>{subject.group}</div>
            </div>
            <div className={classes.pictureListContainer}>
              <ul className={classes.pictureList}>
                {pictures.map((picture) => {
                  return (
                    <li key={picture.uuid}>
                      <EpmsImage uuid={picture.uuid} />
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </Box>
      </CardContent>
      <div className={classes.cardActions}>
        {!pictureSet.bought && digitalSetProduct && !hasNonIPFilesForSale && (
          <div>
            <Divider classes={{ root: classes.divider }} />
            <div className={classes.textContainer}>
              <div className={classes.productDescription}>
                <Trans>Digital portrait set</Trans>
              </div>
              <div className={classes.priceContainer}>
                {rebatedSetPrice && (
                  <>
                    <div className={classes.oldPrice}>
                      {formatCurrency(
                        digitalSetProduct.productprices[0].price,
                      )}
                    </div>
                    <div className={classes.currentPrice}>
                      {formatCurrency(rebatedSetPrice)}
                    </div>
                  </>
                )}
                {!rebatedSetPrice && (
                  <div className={classes.currentPrice}>
                    {formatCurrency(digitalSetProduct.productprices[0].price)}
                  </div>
                )}
              </div>
            </div>
            <Box display="flex" justifyContent="flex-end">
              {!singleFileProducts.length === 0 && (
                <Button
                  color="primary"
                  component={Link}
                  to={generatePath(
                    routes["products.digitalportraits.subject"].path,
                    { subjectUuid: subject.uuid },
                  )}
                >
                  <Trans>View</Trans>
                </Button>
              )}
              <Button
                color="primary"
                onClick={toggleSetInCart()}
                classes={{ root: classes.rightButton }}
              >
                {pictureSet.inCart
                  ? t("Remove from cart")
                  : t("Buy portrait set")}
              </Button>
            </Box>
          </div>
        )}
        {digitalSetProduct && singleFileProducts.length > 0 && (
          <Divider classes={{ root: classes.divider }} />
        )}
        {singleFileProducts.length > 0 && (
          <div>
            <div className={classes.textContainer}>
              <div className={classes.productDescription}>
                {singleFileProducts.length == 1 && digitalPortraitProduct ? (
                  <Trans>Digital portrait picture</Trans>
                ) : (
                  <Trans>Digital pictures</Trans>
                )}
              </div>
              <div className={classes.priceContainer}>
                {
                  // FIXME: What product to show here?
                  rebatedPortraitFilePrice && (
                    <>
                      <div className={classes.oldPrice}>
                        {formatCurrency(
                          digitalPortraitProduct.productprices[0].price,
                        )}
                      </div>
                      <div className={classes.currentPrice}>
                        {t("%s per picture", {
                          postProcess: "sprintf",
                          sprintf: [formatCurrency(rebatedPortraitFilePrice)],
                        })}
                      </div>
                    </>
                  )
                }
              </div>
            </div>
            <Box display="flex" justifyContent="flex-end">
              <Button
                color="primary"
                component={Link}
                to={generatePath(
                  routes["products.digitalportraits.subject"].path,
                  { subjectUuid: subject.uuid },
                )}
                classes={{ root: classes.rightButton }}
              >
                <Trans>Choose pictures</Trans>
              </Button>
            </Box>
          </div>
        )}
      </div>
    </Card>
  );
};
